import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../Auth/AuthContext';
import OrderModal from './OrderModal';
import OCRUploadModal from './OCRUploadModal';
import Modal from '../Common/Modal';
import FilterDropdown from '../Common/FilterDropdown';
import CopyableText from '../Common/CopyableText';
import TicketGenerationCart from '../Invoices/TicketGenerationCart';
import { apiCall } from '../../utils/apiUtils';
import { orderToasts, showToast } from '../../utils/toastUtils';
import { useGlobalSearch } from '../Common/GlobalSearchContext';
import { STATE_ARTICLES, SUPPORTED_STATES } from '../../data/stateArticles';
import LiveStampingPanel from '../LiveStamping/LiveStampingPanel';

const OrdersSkeleton = ({ fadeOut = false }) => (
  <div className={`card-elevated h-full flex flex-col min-h-0 transition-opacity duration-150 ${fadeOut ? 'opacity-20' : 'opacity-100'}`}>
    <div className="px-4 py-4 space-y-4">
      <div className="h-6 w-44 bg-gray-200 rounded-md animate-pulse"></div>
      <div className="space-y-3">
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="h-12 bg-gray-100 rounded-md animate-pulse"></div>
        ))}
      </div>
    </div>
  </div>
);

const TableLoadingOverlay = ({ rows = 6 }) => (
  <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center">
    <div className="w-full px-10 space-y-3">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="h-10 bg-gray-200 rounded-md animate-pulse"></div>
      ))}
    </div>
  </div>
);

// FilterDropdown is now imported from '../Common/FilterDropdown'

const CreateOrderModal = ({ isOpen, onClose, states, formData, setFormData, onSubmit, loading }) => {
  const modalRef = useRef(null);
  const [isInitialOpen, setIsInitialOpen] = useState(false);

  // Custom Combobox State
  const [showDenominationSuggestions, setShowDenominationSuggestions] = useState(false);
  const denominationRef = useRef(null);

  const [showArticleSuggestions, setShowArticleSuggestions] = useState(false);
  const articleRef = useRef(null);

  // Validation state for mobile numbers
  const [firstPartyMobileError, setFirstPartyMobileError] = useState('');
  const [secondPartyMobileError, setSecondPartyMobileError] = useState('');

  // Helper: Remove special characters (allow letters, numbers, spaces, commas, periods)
  const sanitizePropertyDescription = (value) => {
    return value.replace(/[^a-zA-Z0-9\s,.]/g, '');
  };

  // Helper: Remove special characters (allow letters, numbers, and spaces - no symbols)
  const sanitizePartyName = (value) => {
    return value.replace(/[^a-zA-Z0-9\s]/g, '');
  };

  // Helper: Validate Indian mobile number (must start with 6, 7, 8, or 9)
  const validateMobile = (value) => {
    if (!value) return ''; // Empty is OK (will be caught by 'required')
    // Only allow digits
    const digitsOnly = value.replace(/[^0-9]/g, '');
    if (digitsOnly.length > 0 && !['6', '7', '8', '9'].includes(digitsOnly[0])) {
      return 'Mobile number must start with 6, 7, 8, or 9';
    }
    return '';
  };


  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close Denomination Dropdown
      if (denominationRef.current && !denominationRef.current.contains(event.target)) {
        setShowDenominationSuggestions(false);
      }
      // Close Article Dropdown
      if (articleRef.current && !articleRef.current.contains(event.target)) {
        setShowArticleSuggestions(false);
      }
    };

    if (showDenominationSuggestions || showArticleSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDenominationSuggestions, showArticleSuggestions]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen && !isInitialOpen) {
      modalRef.current?.focus();
      setIsInitialOpen(true);
    }

    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }

    return () => {
      window.removeEventListener('keydown', handleEscape);
      if (!isOpen) {
        setIsInitialOpen(false);
      }
    };
  }, [isOpen, onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate quantity (minimum 1)
    if (formData.quantity < 1) {
      return;
    }

    // Validate denomination (1-1500, integers only)
    if (formData.denomination !== '') {
      if (formData.denomination % 1 !== 0) {
        // Error is already shown in UI, just block submit
        return;
      }
      if (formData.denomination < 1 || formData.denomination > 1500) {
        // Error is already shown in UI, just block submit
        return;
      }
    }

    onSubmit(e);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="max-w-4xl"
    >
      {/* Compact Header for Create Order */}
      <div className="flex-shrink-0 px-6 py-3 bg-white flex items-center gap-3">
        <button
          onClick={onClose}
          className="p-1.5 -ml-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all focus:outline-none"
          aria-label="Back"
          title="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <h3 className="text-lg font-semibold text-on-light">Create Order</h3>
      </div>

      {/* Form Content - Side by Side Layout */}
      <div className="flex-1 overflow-y-auto">
        <form onSubmit={handleSubmit} className="h-full flex flex-col px-5 py-3">
          {/* Main Grid: Left = Order Info, Right = Parties */}
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-5">

            {/* LEFT COLUMN: Order Information */}
            <div className="space-y-3">
              <h4 className="font-semibold text-on-light text-base border-b pb-1">Order Information</h4>

              {/* Row 1: Order ID + State */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-on-light mb-1">Order ID</label>
                  <input
                    type="text"
                    value={formData.id}
                    onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                    placeholder="Auto-generate if empty"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-light mb-1">State <span className="text-red-500">*</span></label>
                  <select
                    value={formData.state}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, state: e.target.value, article: '' }));
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="">Select State</option>
                    {states.filter(state => SUPPORTED_STATES.includes(state)).map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Article (full width) */}
              <div className="relative" ref={articleRef}>
                <label className="block text-sm font-medium text-on-light mb-1">Article <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.article}
                    onChange={(e) => {
                      setFormData({ ...formData, article: e.target.value });
                      setShowArticleSuggestions(true);
                    }}
                    onFocus={() => setShowArticleSuggestions(true)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder={formData.state ? `Select Article for ${formData.state}` : "Select State first"}
                    required
                    autoComplete="off"
                    disabled={!formData.state}
                  />
                  <div
                    className={`absolute inset-y-0 right-0 flex items-center px-2 ${!formData.state ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                    onClick={() => formData.state && setShowArticleSuggestions(!showArticleSuggestions)}
                  >
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {showArticleSuggestions && formData.state && STATE_ARTICLES[formData.state] && (
                    <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-auto">
                      {STATE_ARTICLES[formData.state]
                        .filter(article => article.toLowerCase().includes(formData.article.toLowerCase()))
                        .map((article, index) => (
                          <li
                            key={`${article}-${index}`}
                            className="px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 cursor-pointer"
                            onClick={() => {
                              setFormData({ ...formData, article: article });
                              setShowArticleSuggestions(false);
                            }}
                          >
                            {article}
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Row 3: Quantity + Denomination */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-on-light mb-1">Quantity <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    value={formData.quantity || ''}
                    onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || '' })}
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${(formData.quantity && formData.quantity < 1) ? 'border-red-500' : 'border-gray-300'}`}
                    min="1"
                    placeholder="Min 1"
                    required
                  />
                  {formData.quantity && formData.quantity < 1 && (
                    <p className="text-xs text-red-600 mt-1">Min 1</p>
                  )}
                </div>
                <div className="relative" ref={denominationRef}>
                  <label className="block text-sm font-medium text-on-light mb-1">Denomination ₹ <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    value={formData.denomination}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData({ ...formData, denomination: val === '' ? '' : parseFloat(val) });
                      setShowDenominationSuggestions(true);
                    }}
                    onFocus={() => setShowDenominationSuggestions(true)}
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${(formData.denomination !== '' && (formData.denomination % 1 !== 0 || formData.denomination < 1 || formData.denomination > 1500)) ? 'border-red-500' : 'border-gray-300'}`}
                    min="1"
                    max="1500"
                    step="1"
                    placeholder="1-1500"
                    required
                    autoComplete="off"
                  />
                  {showDenominationSuggestions && (
                    <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-auto">
                      {[1, 5, 10, 20, 50, 100, 200, 500, 1000, 1500].map((amount) => (
                        <li
                          key={amount}
                          className="px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 cursor-pointer"
                          onClick={() => {
                            setFormData({ ...formData, denomination: amount });
                            setShowDenominationSuggestions(false);
                          }}
                        >
                          ₹{amount}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {formData.denomination !== '' && formData.denomination % 1 !== 0 && (
                  <p className="text-xs text-red-600 mt-1">No decimal figures accepted.</p>
                )}
                {formData.denomination !== '' && (formData.denomination < 1 || formData.denomination > 1500) && (
                  <p className="text-xs text-red-600 mt-1">Value must be between 1 and 1500.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-on-light mb-1">Consideration Amount (₹)</label>
                <input
                  type="number"
                  value={formData.consideration_amount}
                  onChange={(e) => setFormData({ ...formData, consideration_amount: e.target.value ? parseFloat(e.target.value) : '' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  min="0"
                  step="0.01"
                  aria-label="Consideration Amount (optional)"
                />
              </div>

              {/* Row 4: Consideration + Delivery Mode */}
              <div className="grid grid-cols-2 gap-3">
                {/* <div>
                  <label className="block text-sm font-medium text-on-light mb-1">Consideration ₹</label>
                  <input
                    type="number"
                    value={formData.consideration_amount}
                    onChange={(e) => setFormData({ ...formData, consideration_amount: e.target.value ? parseFloat(e.target.value) : '' })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min="0"
                    step="0.01"
                    placeholder="Optional"
                  />
                </div> */}
                <div>
                  <label className="block text-sm font-medium text-on-light mb-1">Delivery <span className="text-red-500">*</span></label>
                  <select
                    value={formData.delivery_mode}
                    onChange={(e) => setFormData({ ...formData, delivery_mode: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="digital">Digital</option>
                    <option value="physical">Physical</option>
                    <option value="both">Both</option>
                  </select>
                </div>
              </div>

              {/* Delivery Address (conditional) */}
              {(formData.delivery_mode === 'physical' || formData.delivery_mode === 'both') && (
                <div>
                  <label className="block text-sm font-medium text-on-light mb-1">Delivery Address <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.delivery_address}
                    onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 uppercase"
                    placeholder="Physical delivery address"
                    required
                  />
                </div>
              )}

              {/* Property Description */}
              <div>
                <label className="block text-sm font-medium text-on-light mb-1">Property Description</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: sanitizePropertyDescription(e.target.value).toUpperCase().slice(0, 200) })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 uppercase"
                  rows="2"
                  maxLength="200"
                  placeholder="Max 200 chars"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.notes?.length || 0}/200</p>
              </div>
            </div>

            {/* RIGHT COLUMN: Party Details */}
            <div className="space-y-3">
              <h4 className="font-semibold text-on-light text-base border-b pb-1">Party Details</h4>

              {/* Purchased/Paid By */}
              <div className="flex items-center space-x-6">
                <span className="text-sm font-medium text-on-light">Purchased & Paid by:</span>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="purchased_paid_by"
                    value="first_party"
                    checked={formData.purchased_paid_by === 'first_party'}
                    onChange={(e) => setFormData({ ...formData, purchased_paid_by: e.target.value })}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-on-light">First Party</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="purchased_paid_by"
                    value="second_party"
                    checked={formData.purchased_paid_by === 'second_party'}
                    onChange={(e) => setFormData({ ...formData, purchased_paid_by: e.target.value })}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-on-light">Second Party</span>
                </label>
              </div>

              {/* First Party Box */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="font-medium text-black text-sm mb-3">First Party</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-on-light mb-1">Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={formData.first_party.name}
                      onChange={(e) => setFormData({
                        ...formData,
                        first_party: { ...formData.first_party, name: sanitizePartyName(e.target.value).toUpperCase().slice(0, 100) }
                      })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 uppercase"
                      placeholder="Max 100 chars"
                      maxLength="100"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-on-light mb-1">Address</label>
                      <input
                        type="text"
                        value={formData.first_party.address}
                        onChange={(e) => setFormData({
                          ...formData,
                          first_party: { ...formData.first_party, address: e.target.value.toUpperCase() }
                        })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 uppercase"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-on-light mb-1">Mobile <span className="text-red-500">*</span></label>
                      <input
                        type="tel"
                        value={formData.first_party.mobile}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                          setFormData({
                            ...formData,
                            first_party: { ...formData.first_party, mobile: value }
                          });
                          setFirstPartyMobileError(validateMobile(value));
                        }}
                        className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${firstPartyMobileError ? 'border-red-500' : 'border-gray-300'}`}
                        pattern="[6-9][0-9]{9}"
                        maxLength="10"
                        placeholder="10 digits"
                        required
                      />
                      {firstPartyMobileError && (
                        <p className="text-xs text-red-600 mt-1">{firstPartyMobileError}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Second Party Box */}
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="font-medium text-black text-sm mb-3">Second Party (Optional)</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-on-light mb-1">Name</label>
                    <input
                      type="text"
                      value={formData.second_party.name}
                      onChange={(e) => setFormData({
                        ...formData,
                        second_party: { ...formData.second_party, name: sanitizePartyName(e.target.value).toUpperCase().slice(0, 100) }
                      })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 uppercase"
                      placeholder="Max 100 chars"
                      maxLength="100"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-on-light mb-1">Address</label>
                      <input
                        type="text"
                        value={formData.second_party.address}
                        onChange={(e) => setFormData({
                          ...formData,
                          second_party: { ...formData.second_party, address: e.target.value.toUpperCase() }
                        })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 uppercase"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-on-light mb-1">Mobile</label>
                      <input
                        type="tel"
                        value={formData.second_party.mobile}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                          setFormData({
                            ...formData,
                            second_party: { ...formData.second_party, mobile: value }
                          });
                          setSecondPartyMobileError(validateMobile(value));
                        }}
                        className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${secondPartyMobileError ? 'border-red-500' : 'border-gray-300'}`}
                        pattern="[6-9][0-9]{9}"
                        maxLength="10"
                        placeholder="10 digits"
                      />
                      {secondPartyMobileError && (
                        <p className="text-xs text-red-600 mt-1">{secondPartyMobileError}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end space-x-3 pt-4 mt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-on-light border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Order'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

const OrderManagement = ({
  pendingSelection = null,
  onSelectionHandled = () => { },
  initialTab = 'bulk',
  showTabSwitcher = true
}) => {
  const { user, loading, logout } = useAuth();
  const { setSubmitHandler, setGlobalSearchHandler } = useGlobalSearch();
  const [orders, setOrders] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [states, setStates] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [clients, setClients] = useState([]);
  const [firstPartyNames, setFirstPartyNames] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [selectedOrderLoading, setSelectedOrderLoading] = useState(false);
  const [loadingOrderId, setLoadingOrderId] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    state: '',
    article: '',
    quantity: '',
    denomination: '',
    consideration_amount: '',
    purchased_paid_by: 'first_party',
    first_party: { name: '', address: '', mobile: '' },
    second_party: { name: '', address: '', mobile: '' },
    delivery_mode: 'digital',
    delivery_address: '',
    notes: ''
  });
  const [fetchLoading, setFetchLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    state: '',
    status: '',
    start_date: '',
    end_date: '',
    first_party_name: '',
    client_name: '',
    vendor_name: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [goToPage, setGoToPage] = useState('');
  const ordersPerPage = 20;
  const [totalOrders, setTotalOrders] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const [orderViewTab, setOrderViewTab] = useState(initialTab); // 'bulk' or 'live'

  // Sync internal tab state when prop changes (for sidebar navigation)
  useEffect(() => {
    if (initialTab) {
      setOrderViewTab(initialTab);
    }
  }, [initialTab]);

  // Prevent browser window scrolling while on orders page; restore on unmount
  useEffect(() => {
    const previousOverflowY = document.body.style.overflowY;
    document.body.style.overflowY = 'hidden';
    return () => {
      document.body.style.overflowY = previousOverflowY;
    };
  }, []);

  // Ticket Generation Cart state
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [showCart, setShowCart] = useState(false);
  const [availableOrders, setAvailableOrders] = useState([]);
  // Quick actions state
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [showOCRModal, setShowOCRModal] = useState(false);
  const [ocrOrder, setOcrOrder] = useState(null);
  const orderDetailsCache = useRef(new Map());
  const filterButtonRef = useRef(null);
  const tableScrollRef = useRef(null);
  const quickActionsRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const [scrollAreaHeight, setScrollAreaHeight] = useState(null);

  // Helper to refresh the currently selected order details without closing the modal
  const refreshSelectedOrder = async (orderId) => {
    // If no specific orderId is passed, use the current selectedOrder
    const targetId = orderId || selectedOrder?.id;
    if (!targetId) return;

    try {
      // Invalidate cache for this order
      if (orderDetailsCache.current.has(targetId)) {
        orderDetailsCache.current.delete(targetId);
      }

      const token = localStorage.getItem('token');
      const response = await apiCall(`/api/orders/${targetId}`, { method: 'GET' }, token, logout);

      if (response?.order) {
        // Update cache
        orderDetailsCache.current.set(targetId, response.order);

        // Update state if this order is still selected
        setSelectedOrder(prev => {
          if (prev && prev.id === targetId) {
            return response.order;
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Error refreshing order details:', error);
    }
  };

  // Update scrollable table height to fill viewport (keeping browser from scrolling)
  useEffect(() => {
    const updateScrollHeight = () => {
      if (!tableScrollRef.current) return;
      const rect = tableScrollRef.current.getBoundingClientRect();
      const quickActionsHeight = quickActionsRef.current ? quickActionsRef.current.getBoundingClientRect().height : 0;
      const padding = 16; // spacing buffer
      const available = Math.max(200, window.innerHeight - rect.top - quickActionsHeight - padding);
      setScrollAreaHeight(available);
    };

    updateScrollHeight();
    window.addEventListener('resize', updateScrollHeight);
    return () => {
      window.removeEventListener('resize', updateScrollHeight);
    };
  }, [
    orders.length,
    filters.state,
    filters.status,
    filters.start_date,
    filters.end_date,
    filters.first_party_name,
    filters.client_name,
    filters.vendor_name,
    user.role,
    showFilters,
    showMobileActions,
    showCreateForm,
    selectedOrderIds.size,
  ]);

  // Keep orders per page constant (20)

  const hasInitializedRef = useRef(false);
  const lastUserIdRef = useRef(null);

  useEffect(() => {
    if (!loading && user) {
      if (lastUserIdRef.current !== user.id) {
        lastUserIdRef.current = user.id;
        hasInitializedRef.current = false;
        orderDetailsCache.current.clear();
      }

      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        fetchOrders();
        fetchStates();
        fetchAllStates();
        if (user.role === 'admin') {
          fetchVendors();
          fetchClients();
        }
      }
    }
  }, [loading, user]);

  // Connect global search pill submit to orders search
  useEffect(() => {
    setSubmitHandler((q) => {
      const nextQuery = (q || '').trim();
      setSearchQuery(nextQuery);
      setCurrentPage(1);
      if (nextQuery) {
        searchOrdersWithQuery(nextQuery);
      } else {
        fetchOrders();
      }
    });

    // Set up global search handler for navigation
    setGlobalSearchHandler((q) => {
      const nextQuery = (q || '').trim();
      if (nextQuery) {
        // Search in orders
        setSearchQuery(nextQuery);
        setCurrentPage(1);
        searchOrdersWithQuery(nextQuery);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const uniqueFirstPartyNames = [...new Set(orders.map(order => order.first_party?.name).filter(name => name && name !== 'N/A'))].sort();
    setFirstPartyNames(uniqueFirstPartyNames);
  }, [orders]);

  const fetchOrders = async (page) => {
    // If page not provided, default to currentPage (or 1)
    if (typeof page === 'undefined' || page === null) page = currentPage || 1;
    setFetchLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await apiCall(`/api/orders?page=${page}&limit=${ordersPerPage}`, { method: 'GET' }, token, logout);
      const respOrders = response.orders || [];
      setOrders(respOrders);

      // Try multiple possible total keys returned by backend
      const total = response.total || response.total_count || response.count || response.totalOrders || 0;
      setTotalOrders(Number(total) || respOrders.length);

      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching orders:', error);
      showToast.error('Failed to fetch orders');
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiCall('/api/users/vendor', { method: 'GET' }, token, logout);
      setVendors(response.users);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      showToast.error('Failed to fetch vendors');
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiCall('/api/admin/clients', { method: 'GET' }, token, logout);
      // API may return an array directly or an object like { clients: [...] }
      const clientsData = Array.isArray(response) ? response : (response?.clients || []);
      setClients(clientsData);
    } catch (error) {
      console.error('Error fetching clients:', error);
      showToast.error('Failed to fetch clients');
    }
  };

  const fetchStates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiCall('/api/states/from-orders', { method: 'GET' }, token, logout);
      // API may return an array directly or an object like { states: [...] }
      const statesData = Array.isArray(response) ? response : (response?.states || []);
      setStates(statesData);
    } catch (error) {
      console.error('Error fetching states:', error);
      showToast.error('Failed to fetch states');
    }
  };

  const fetchAllStates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiCall('/api/states', { method: 'GET' }, token, logout);
      setAllStates(response.states);
    } catch (error) {
      console.error('Error fetching all states:', error);
      showToast.error('Failed to fetch all states');
    }
  };

  const createOrder = async (e) => {
    e.preventDefault();
    setFetchLoading(true);
    try {
      const token = localStorage.getItem('token');
      const orderData = {
        ...formData,
        id: formData.id.trim() || undefined, // Send undefined if empty to let backend generate
        consideration_amount: formData.consideration_amount || undefined, // Send undefined if empty
        second_party: formData.second_party.name.trim() ? formData.second_party : null
      };
      await apiCall('/api/orders', {
        method: 'POST',
        body: JSON.stringify(orderData),
      }, token, logout);
      setFormData({
        id: '',
        state: '',
        article: '',
        quantity: 1,
        denomination: 0,
        consideration_amount: '',
        purchased_paid_by: 'first_party',
        first_party: { name: '', address: '', mobile: '' },
        second_party: { name: '', address: '', mobile: '' },
        delivery_mode: 'digital',
        notes: ''
      });
      setShowCreateForm(false);
      fetchOrders();
      orderToasts.placed();
    } catch (error) {
      console.error('Error creating order:', error);
      showToast.error(error.message || 'Failed to create order. Please try again.');
    } finally {
      setFetchLoading(false);
    }
  };

  const assignOrder = async (orderId, vendorId) => {
    try {
      const token = localStorage.getItem('token');
      await apiCall('/api/orders/assign', {
        method: 'POST',
        body: JSON.stringify({ order_id: orderId, vendor_id: vendorId }),
      }, token, logout);
      fetchOrders();
      await refreshSelectedOrder(orderId);
    } catch (error) {
      console.error('Error assigning order:', error);
      orderToasts.assignmentError();
    }
  };

  const approveRejectSample = async (orderId, approved) => {
    try {
      const token = localStorage.getItem('token');
      await apiCall(`/api/orders/${orderId}/approve-sample`, {
        method: 'POST',
        body: JSON.stringify({ order_id: orderId, approved }),
      }, token, logout);
      fetchOrders();
      await refreshSelectedOrder(orderId);
      if (approved) {
        orderToasts.sampleApproved();
      } else {
        orderToasts.sampleRejected();
      }
      // setSelectedOrder(null); // Removed to keep modal open with updated status
    } catch (error) {
      console.error('Error approving/rejecting sample:', error);
      orderToasts.approvalError();
    }
  };

  const handleFileUpload = async (orderId, fileType, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/${fileType}`, {
        method: 'POST',
        credentials: 'include',  // Send HTTP-only cookie
        body: formData,
      });
      if (response.ok) {
        fetchOrders();
        await refreshSelectedOrder(orderId);
        showToast.success(`${fileType.replace('-', ' ')} uploaded successfully`);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      showToast.error(`Failed to upload ${fileType.replace('-', ' ')}`);
    }
  };



  const getStatusColor = (status) => {
    const colors = {
      placed: 'status-placed',
      assigned: 'status-assigned',
      sample_submitted: 'status-sample_submitted',
      sample_approved: 'status-sample_approved',
      sample_rejected: 'status-sample_rejected',
      executing: 'status-executing',
      completed: 'status-completed',
      delivered: 'status-delivered',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      placed: '',
      assigned: '',
      sample_submitted: '',
      sample_approved: '',
      sample_rejected: '',
      executing: '',
      completed: '',
      delivered: '',
    };
  };

  const searchOrdersWithQuery = async (q, page = 1) => {
    if (!q || !q.trim()) {
      fetchOrders(page);
      return;
    }
    setFetchLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await apiCall(`/api/orders/search?query=${encodeURIComponent(q)}&page=${page}&limit=${ordersPerPage}`, { method: 'GET' }, token, logout);
      const respOrders = response.orders || [];
      setOrders(respOrders);
      const total = response.total || response.total_count || response.count || response.totalOrders || 0;
      setTotalOrders(Number(total) || respOrders.length);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error searching orders:', error);
      showToast.error('Failed to search orders');
    } finally {
      setFetchLoading(false);
    }
  };

  const filterOrders = async () => {
    const params = new URLSearchParams();
    if (filters.state) params.append('state', filters.state);
    if (filters.status) params.append('status', filters.status);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.first_party_name) params.append('first_party_name', filters.first_party_name);
    if (filters.client_name && user.role === 'admin') params.append('client_name', filters.client_name);
    if (filters.vendor_name && user.role === 'admin') params.append('vendor_name', filters.vendor_name);
    setFetchLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await apiCall(`/api/orders/filter?${params.toString()}&page=1&limit=${ordersPerPage}`, { method: 'GET' }, token, logout);
      const respOrders = response.orders || [];
      setOrders(respOrders);
      const total = response.total || response.total_count || response.count || response.totalOrders || 0;
      setTotalOrders(Number(total) || respOrders.length);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error filtering orders:', error);
      showToast.error('Failed to filter orders');
    } finally {
      setFetchLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      state: '',
      status: '',
      start_date: '',
      end_date: '',
      first_party_name: '',
      client_name: '',
      vendor_name: ''
    });
    setSearchQuery('');
    setCurrentPage(1);
    fetchOrders();
  };

  const editOrder = async (orderId, orderData) => {
    try {
      const token = localStorage.getItem('token');
      await apiCall(`/api/orders/${orderId}`, {
        method: 'PUT',
        body: JSON.stringify(orderData),
      }, token, logout);
      fetchOrders();
      await refreshSelectedOrder(orderId);
      orderToasts.updated();
    } catch (error) {
      console.error('Error updating order:', error);
      showToast.error('Failed to update order');
    }
  };

  const rejectOrder = async (orderId, cancellationReason) => {
    try {
      const token = localStorage.getItem('token');
      await apiCall(`/api/orders/${orderId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ order_id: orderId, cancellation_reason: cancellationReason }),
      }, token, logout);
      fetchOrders();
      await refreshSelectedOrder(orderId);
      orderToasts.rejected();
    } catch (error) {
      console.error('Error rejecting order:', error);
      showToast.error('Failed to reject order');
    }
  };

  const completeOrder = async (orderId) => {
    try {
      const token = localStorage.getItem('token');
      await apiCall(`/api/orders/${orderId}/complete`, { method: 'POST' }, token, logout);
      fetchOrders();
      await refreshSelectedOrder(orderId);
      orderToasts.completed();
    } catch (error) {
      console.error('Error completing order:', error);
      showToast.error('Failed to complete order');
    }
  };

  const uploadFullOrderDocument = async (orderId, files, pagesPerStamp = 1, csvFile = null) => {
    try {
      const formData = new FormData();

      // Handle both single file (backward compatibility) and multiple files
      if (Array.isArray(files)) {
        files.forEach(file => {
          formData.append('files', file);
        });
      } else {
        formData.append('files', files);
      }

      // Add pages per stamp parameter
      formData.append('pages_per_stamp', pagesPerStamp.toString());

      // Add CSV file for certificate ID mapping (admin only)
      if (csvFile) {
        formData.append('csv_file', csvFile);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/full-document`, {
        method: 'POST',
        credentials: 'include',  // Send HTTP-only cookie
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        fetchOrders();
        await refreshSelectedOrder(orderId);
        showToast.success(result.message);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading full order document:', error);
      showToast.error(error.message || 'Failed to upload full order document');
      throw error; // Re-throw so caller knows it failed
    }
  };

  // Ticket Generation Functions
  const handleCheckboxChange = (orderId, checked) => {
    const newSelectedIds = new Set(selectedOrderIds);
    if (checked) {
      newSelectedIds.add(orderId);
    } else {
      newSelectedIds.delete(orderId);
    }
    setSelectedOrderIds(newSelectedIds);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedOrderIds(new Set(currentOrders.map(order => order.id)));
    } else {
      setSelectedOrderIds(new Set());
    }
  };

  const handleAddToCart = async () => {
    if (selectedOrderIds.size === 0) {
      showToast.error('Please select at least one order');
      return;
    }

    try {
      // Get available orders for invoice
      const response = await apiCall('/api/orders/available-for-invoice');
      const availableOrdersList = response.orders || [];

      // Filter selected orders that are available for invoicing
      const selectedAvailableOrders = availableOrdersList.filter(order =>
        selectedOrderIds.has(order.id)
      );

      if (selectedAvailableOrders.length === 0) {
        showToast.error('Selected orders are not available for invoicing');
        return;
      }

      setAvailableOrders(selectedAvailableOrders);
      setShowCart(true);
    } catch (error) {
      console.error('Error fetching available orders:', error);
      showToast.error('Failed to prepare cart');
    }
  };

  const handleInvoiceGenerated = () => {
    setSelectedOrderIds(new Set());
    fetchOrders();
  };

  const isOrderSelectable = (order) => {
    return (user.role === 'admin' || user.role === 'accountant') &&
      order.total_stamp_duty &&
      (order.paid_stamp_duty || 0) < order.total_stamp_duty &&
      order.status !== 'rejected';
  };

  // Bulk reject selected orders (Admin): requires confirmation + reason
  const handleBulkReject = async () => {
    if (selectedOrderIds.size === 0) return;
    if (!rejectReason.trim()) {
      showToast.error('Please provide a cancellation reason');
      return;
    }
    setBulkActionLoading(true);
    try {
      for (const orderId of Array.from(selectedOrderIds)) {
        await rejectOrder(orderId, rejectReason.trim());
      }
      setRejectReason('');
      setShowRejectDialog(false);
      setSelectedOrderIds(new Set());
      fetchOrders();
    } catch (e) {
      showToast.error('Failed to reject one or more orders');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((totalOrders || orders.length) / ordersPerPage));
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Server-driven pagination: `orders` holds only the current page items
  const currentOrders = orders;
  const startOrder = totalOrders === 0 ? 0 : (currentPage - 1) * ordersPerPage + 1;
  const endOrder = Math.min(currentPage * ordersPerPage, totalOrders || orders.length);

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setFadeOut(true);
      setTimeout(() => {
        fetchOrders(currentPage - 1);
        setFadeOut(false);
      }, 150);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setFadeOut(true);
      setTimeout(() => {
        fetchOrders(currentPage + 1);
        setFadeOut(false);
      }, 150);
    }
  };

  const handleGoToPage = (e) => {
    if (e.key === 'Enter') {
      const pageNumber = parseInt(goToPage);
      if (pageNumber >= 1 && pageNumber <= totalPages) {
        setGoToPage('');
        setFadeOut(true);
        setTimeout(() => {
          fetchOrders(pageNumber);
          setFadeOut(false);
        }, 150);
      } else {
        showToast.error(`Page number must be between 1 and ${totalPages}`);
      }
    }
  };

  const openOrderDetails = useCallback(async (order) => {
    if (!order) return;

    setSelectedOrder(order);
    setIsOrderModalOpen(true);

    const cachedOrder = orderDetailsCache.current.get(order.id);
    if (cachedOrder) {
      setSelectedOrder(cachedOrder);
      setSelectedOrderLoading(false);
      setLoadingOrderId(null);
      return;
    }

    setSelectedOrderLoading(true);
    setLoadingOrderId(order.id);

    try {
      const response = await apiCall(`/api/orders/${order.id}`, { method: 'GET' });
      if (response?.order) {
        orderDetailsCache.current.set(order.id, response.order);
        setSelectedOrder((current) => {
          if (!current || current.id !== order.id) {
            return current;
          }
          return response.order;
        });
      }
    } catch (error) {
      console.error('Error loading order details:', error);
      showToast.error('Failed to load order details');
    } finally {
      setSelectedOrderLoading(false);
      setLoadingOrderId(null);
    }
  }, [showToast]);

  useEffect(() => {
    if (!pendingSelection || pendingSelection.type !== 'order') {
      return;
    }

    const selectionOrder = pendingSelection.item;
    if (!selectionOrder) {
      onSelectionHandled();
      return;
    }

    const selectionId = selectionOrder.id || selectionOrder.order_id || selectionOrder.orderId;
    if (!selectionId) {
      onSelectionHandled();
      return;
    }

    const existingOrder = orders.find((o) => String(o.id) === String(selectionId));
    const orderToOpen = existingOrder || { ...selectionOrder, id: selectionId };

    openOrderDetails(orderToOpen).finally(onSelectionHandled);
  }, [pendingSelection, orders, openOrderDetails, onSelectionHandled]);

  const isInitialLoading = (loading || fetchLoading) && orders.length === 0;

  if (isInitialLoading) {
    return <OrdersSkeleton fadeOut={fadeOut} />;
  }

  if (!user) {
    return <div>Please log in</div>;
  }

  // Derived applied filters for chips
  const appliedFilters = [
    filters.state ? { key: 'state', label: 'State', value: filters.state } : null,
    filters.status ? { key: 'status', label: 'Status', value: filters.status } : null,
    filters.start_date ? { key: 'start_date', label: 'Start', value: filters.start_date } : null,
    filters.end_date ? { key: 'end_date', label: 'End', value: filters.end_date } : null,
    filters.first_party_name ? { key: 'first_party_name', label: 'First Party', value: filters.first_party_name } : null,
    (user.role === 'admin' && filters.client_name) ? { key: 'client_name', label: 'Client', value: filters.client_name } : null,
    (user.role === 'admin' && filters.vendor_name) ? { key: 'vendor_name', label: 'Vendor', value: filters.vendor_name } : null,
  ].filter(Boolean);

  const hasAnyFilterApplied = appliedFilters.length > 0;

  const clearSingleFilter = (key) => {
    const next = { ...filters, [key]: '' };
    setFilters(next);
    setCurrentPage(1);
    const params = new URLSearchParams();
    if (next.state) params.append('state', next.state);
    if (next.status) params.append('status', next.status);
    if (next.start_date) params.append('start_date', next.start_date);
    if (next.end_date) params.append('end_date', next.end_date);
    if (next.first_party_name) params.append('first_party_name', next.first_party_name);
    if (next.client_name && user.role === 'admin') params.append('client_name', next.client_name);
    if (next.vendor_name && user.role === 'admin') params.append('vendor_name', next.vendor_name);
    params.append('page', 1);
    params.append('limit', ordersPerPage);
    (async () => {
      setFetchLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await apiCall(`/api/orders/filter?${params.toString()}`, { method: 'GET' }, token, logout);
        const respOrders = response.orders || [];
        setOrders(respOrders);
        const total = response.total || response.total_count || response.count || respOrders.length;
        setTotalOrders(Number(total));
      } catch (e) {
        showToast.error('Failed to filter orders');
      } finally {
        setFetchLoading(false);
      }
    })();
  };

  return (
    <div className="card-elevated h-full flex flex-col min-h-0">
      {/* Order Type Tab Switcher */}
      {showTabSwitcher && (
        <div className="flex-shrink-0 px-4 pt-0 pb-1 bg-white rounded-t-xl border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
            {/* <button
              onClick={() => setOrderViewTab('bulk')}
              className={`px-4 py-1 rounded-md text-sm font-medium transition-all ${orderViewTab === 'bulk'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Bulk Orders
            </button> */}
            <button
              onClick={() => setOrderViewTab('bulk')}
              className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all flex items-center ${orderViewTab === 'bulk'
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Bulk Orders
              <span className="ml-2 relative flex h-2 w-2">
                {/* <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span> */}
                {/* <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span> */}
              </span>
            </button>

            <button
              onClick={() => setOrderViewTab('live')}
              className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all flex items-center ${orderViewTab === 'live'
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Live Stamping
              <span className="ml-2 relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
              </span>
            </button>
            </div>
            <span className="text-xs text-gray-400">{orderViewTab === 'bulk' ? 'Standard order workflow' : 'Single-stamp instant service'}</span>
          </div>
        </div>
      )}

      {/* Live Stamping Tab Content */}
      {
        orderViewTab === 'live' ? (
          <div className="flex-1 min-h-0 overflow-hidden p-2 flex flex-col">
            <LiveStampingPanel />
          </div>
        ) : (
          /* Bulk Orders Tab Content */
          <>
            <div className="flex-shrink-0 px-4 py-1 bg-white">
              <div className="flex justify-between items-center mb-2">
                {/* Left: Filters and chips (desktop) */}
                <div className="hidden sm:flex items-center gap-2">
                  {/* Search Input */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search orders (ID, article, party name...)"
                      value={searchQuery}
                      onChange={(e) => {
                        const query = e.target.value;
                        setSearchQuery(query);
                        setCurrentPage(1);

                        // Clear previous debounce timer
                        if (searchDebounceRef.current) {
                          clearTimeout(searchDebounceRef.current);
                        }

                        // Set new debounce timer (500ms delay)
                        searchDebounceRef.current = setTimeout(() => {
                          if (query.trim()) {
                            setFadeOut(true);
                            setTimeout(() => {
                              searchOrdersWithQuery(query.trim(), 1);
                              setFadeOut(false);
                            }, 150);
                          } else {
                            setFadeOut(true);
                            setTimeout(() => {
                              fetchOrders(1);
                              setFadeOut(false);
                            }, 150);
                          }
                        }, 500);
                      }}
                      className="px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 w-40"
                      aria-label="Search orders"
                    />
                  </div>
                  <div className="relative">
                    <button
                      ref={filterButtonRef}
                      onClick={() => setShowFilters(!showFilters)}
                      className="btn-secondary text-xs px-2.5 py-1 h-8 inline-flex items-center hover-lift"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707v4.172a1 1 0 01-.464.864l-3.5 2.333A1 1 0 019 20.586V15.414a1 1 0 00-.293-.707L2.293 8.293A1 1 0 012 7.586V4z" />
                      </svg>
                      Filters
                    </button>
                    <FilterDropdown
                      isOpen={showFilters}
                      onClose={() => setShowFilters(false)}
                      filters={filters}
                      setFilters={setFilters}
                      onApplyFilters={() => {
                        filterOrders();
                        setShowFilters(false);
                      }}
                      buttonRef={filterButtonRef}
                      title="Filter Orders"
                      showState={true}
                      showStatus={true}
                      showDates={true}
                      showFirstParty={true}
                      showClient={user.role === 'admin'}
                      showVendor={user.role === 'admin'}
                      states={states}
                      firstPartyNames={firstPartyNames}
                      clients={clients}
                      vendors={vendors}
                    />
                  </div>
                  {hasAnyFilterApplied && (
                    <button
                      onClick={clearFilters}
                      className="btn-danger text-xs px-2.5 py-1 h-8 hover-lift"
                    >
                      Clear
                    </button>
                  )}
                  {/* Applied filter chips */}
                  <div className="flex flex-wrap gap-2">
                    {appliedFilters.map(({ key, label, value }) => (
                      <span key={key} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 border border-blue-200">
                        <span className="mr-1 font-medium">{label}:</span>
                        <span className="mr-1">{value}</span>
                        <button onClick={() => clearSingleFilter(key)} className="text-blue-700 hover:text-blue-900" aria-label={`Remove ${label} filter`}>
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
                {/* Right: Create Order (desktop) + Mobile actions */}
                <div className="hidden sm:flex gap-2 items-center">
                  {(user.role === 'client' || user.role === 'admin') && (
                    <button
                      onClick={() => setShowCreateForm(true)}
                      className="btn-primary text-xs px-2.5 py-1 h-8 inline-flex items-center hover-lift"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Create Order
                    </button>
                  )}
                </div>
                {/* Mobile actions */}
                <div className="sm:hidden relative w-full">
                  {/* Mobile search bar */}
                  <div className="mb-2">
                    <input
                      type="text"
                      placeholder="Search orders..."
                      value={searchQuery}
                      onChange={(e) => {
                        const query = e.target.value;
                        setSearchQuery(query);
                        setCurrentPage(1);

                        if (searchDebounceRef.current) {
                          clearTimeout(searchDebounceRef.current);
                        }

                        searchDebounceRef.current = setTimeout(() => {
                          if (query.trim()) {
                            setFadeOut(true);
                            setTimeout(() => {
                              searchOrdersWithQuery(query.trim(), 1);
                              setFadeOut(false);
                            }, 150);
                          } else {
                            setFadeOut(true);
                            setTimeout(() => {
                              fetchOrders(1);
                              setFadeOut(false);
                            }, 150);
                          }
                        }, 500);
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      aria-label="Search orders"
                    />
                  </div>
                  <button
                    onClick={() => setShowMobileActions((v) => !v)}
                    className="btn-secondary text-sm px-3 py-1 h-9 inline-flex items-center hover-lift w-full justify-center"
                    aria-haspopup="menu"
                    aria-expanded={showMobileActions}
                  >
                    Actions
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showMobileActions && (
                    <div className="absolute right-0 mt-2 w-44 bg-white rounded-md shadow-lg border border-gray-200 z-20">
                      <button
                        onClick={() => {
                          setShowMobileActions(false);
                          setShowFilters(true);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                      >
                        Filters
                      </button>
                      <button
                        onClick={() => {
                          setShowMobileActions(false);
                          clearFilters();
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                      >
                        Clear
                      </button>
                      {(user.role === 'client' || user.role === 'admin') && (
                        <button
                          onClick={() => {
                            setShowMobileActions(false);
                            setShowCreateForm(true);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                        >
                          Create Order
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <CreateOrderModal
              isOpen={showCreateForm}
              onClose={() => setShowCreateForm(false)}
              states={allStates}
              formData={formData}
              setFormData={setFormData}
              onSubmit={createOrder}
              loading={fetchLoading}
            />

            {orders.length > 0 && (
              <div className="pagination-container p-4 h-9 border border-stone-200 rounded-t-xl flex-shrink-0">
                <div className="pagination-info text-sm">
                  Showing {startOrder} to {endOrder} of {totalOrders} orders
                </div>
                <div className="pagination-controls flex items-center space-x-3">
                  {/* Previous */}
                  <button
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    className="pagination-btn text-sm px-1.5 py-0.5 h-6 w-8 relative"
                    aria-label="Previous page"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  {/* Go to page: 1 / X */}
                  <div className="flex items-center space-x-1.5">
                    <span className="text-sm text-on-light">Go to page:</span>

                    <input
                      type="number"
                      min="1"
                      max={totalPages}
                      value={goToPage}
                      onChange={(e) => setGoToPage(e.target.value)}
                      onKeyPress={handleGoToPage}
                      placeholder={currentPage.toString()}
                      className="goto-page-input text-xs py-0.5 w-10 text-center"
                    />

                    <span className="text-sm font-medium">/ {totalPages}</span>
                  </div>

                  {/* Next */}
                  <button
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    className="pagination-btn text-sm px-1.5 py-0.5 h-6 w-8 relative"
                    aria-label="Next page"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            <div className="table-container-box bg-white border border-stone-200 border-t-0 rounded-b-xl shadow-sm flex-1 min-h-0 overflow-hidden flex flex-col relative">
              <div
                ref={tableScrollRef}
                className={`table-scroll-area min-w-0 overflow-auto transition-opacity duration-150 ${fadeOut ? 'opacity-20' : 'opacity-100'}`}
                style={scrollAreaHeight ? { height: scrollAreaHeight, flex: '0 1 auto' } : { flex: '1 1 auto', minHeight: 0 }}
              >
                <table className="table table-compact w-full" style={{ fontSize: '13px' }}>
                  <thead className="sticky top-0 bg-gray-200 z-10">
                    <tr className="h-8"> {/* Fixed height for slimmer header */}
                      {(user.role === 'admin' || user.role === 'accountant') && (
                        <th className="py-0 px-2 text-xs font-semibold text-gray-700 w-8 text-center align-middle">
                          <input
                            type="checkbox"
                            checked={selectedOrderIds.size > 0 && selectedOrderIds.size === currentOrders.length}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="form-checkbox h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 border-gray-400 rounded transition duration-150 ease-in-out cursor-pointer"
                          />
                        </th>
                      )}
                      <th className="py-0 px-2 text-xs font-semibold text-gray-700 text-left whitespace-nowrap align-middle">ORDER ID</th>
                      <th className="py-0 px-2 text-xs font-semibold text-gray-700 text-left whitespace-nowrap align-middle">ORDER DATE</th>
                      <th className="py-0 px-2 text-xs font-semibold text-gray-700 text-left whitespace-nowrap align-middle">
                        <div className="truncate">STATE</div>
                      </th>
                      {user.role === 'admin' && (
                        <th className="py-0 px-2 text-xs font-semibold text-gray-700 text-left whitespace-nowrap align-middle">
                          <div className="truncate">CLIENT NAME</div>
                        </th>
                      )}
                      <th className="py-0 px-2 text-xs font-semibold text-gray-700 text-left whitespace-nowrap w-[15%] max-w-[150px] align-middle">
                        <div className="truncate">FIRST PARTY</div>
                      </th>
                      <th className="py-0 px-2 text-xs font-semibold text-gray-700 text-left whitespace-nowrap align-middle">DENOMINATION</th>
                      <th className="py-0 px-2 text-xs font-semibold text-gray-700 text-center whitespace-nowrap align-middle">QUANTITY</th>
                      <th className="py-0 px-2 text-xs font-semibold text-gray-700 text-center whitespace-nowrap align-middle">
                        <div className="truncate w-full text-center">STATUS</div>
                      </th>
                      <th className="py-0 px-2 text-xs font-semibold text-gray-700 text-center whitespace-nowrap w-[15%] align-middle">
                        <div className="w-full text-center">UPLOAD PROGRESS</div>
                      </th>
                      <th className="py-0 px-1 w-[1px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentOrders.map((order, index) => {
                      const pagesUploaded = order.total_pages_uploaded || 0;
                      const totalPages = order.quantity || 1;
                      const progressPercentage = totalPages > 0 ? Math.min((pagesUploaded / totalPages) * 100, 100) : 0;
                      return (
                        <tr
                          key={order.id}
                          onClick={() => openOrderDetails(order)}
                          className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                            } hover:bg-stone-100 transition-colors cursor-pointer group`}
                        >
                          {(user.role === 'admin' || user.role === 'accountant') && (
                            <td className="py-2 px-1" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedOrderIds.has(order.id)}
                                onChange={(e) => handleCheckboxChange(order.id, e.target.checked)}
                                className="form-checkbox h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            </td>
                          )}
                          <td className="py-2 px-1">
                            <CopyableText text={order.id}>
                              <span className="font-mono text-xs text-on-light">{order.id}</span>
                            </CopyableText>
                          </td>
                          <td className="py-2 px-1">
                            <div className="text-xs text-on-light">
                              <CopyableText text={order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN') : 'N/A'}>
                                {order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN') : 'N/A'}
                              </CopyableText>
                            </div>
                          </td>
                          <td className="py-2 px-1">
                            <div className="text-xs text-on-light break-words whitespace-normal line-clamp-2">
                              {order.state}
                            </div>
                          </td>
                          {user.role === 'admin' && (
                            <td className="py-2 px-1">
                              <div className="text-xs text-on-light break-words whitespace-normal line-clamp-2">
                                <CopyableText text={order.client_name || 'N/A'}>
                                  {order.client_name || 'N/A'}
                                </CopyableText>
                              </div>
                            </td>
                          )}
                          <td className="py-2 px-1">
                            <div className="text-xs text-on-light break-words whitespace-normal line-clamp-2">
                              {order.first_party?.name || 'N/A'}
                            </div>
                          </td>
                          <td className="py-2 px-1">
                            <div className="text-xs font-medium text-on-light">₹{order.denomination}</div>
                          </td>
                          <td className="py-2 px-1">
                            <div className="text-xs text-on-light">{order.quantity}</div>
                          </td>
                          <td className="py-2 px-1 text-center align-middle">
                            <div className="break-words whitespace-normal flex justify-center">
                              <span className={`status-badge ${getStatusColor(order.status)} text-xs inline-block`}>
                                {getStatusIcon(order.status)} {order.status.replace('_', ' ')}
                              </span>
                            </div>
                          </td>
                          <td className="py-2 px-1">
                            <div className="flex flex-col items-start">
                              <progress
                                className="w-full h-2 bg-gray-200 rounded"
                                value={progressPercentage}
                                max="100"
                                aria-label={`Page progress: ${pagesUploaded} of ${totalPages} pages`}
                              ></progress>
                              <span className="text-xs text-on-light mt-1">
                                {pagesUploaded}/{totalPages} pages
                              </span>
                            </div>
                          </td>
                          <td className="py-2 px-0 whitespace-nowrap text-center w-[1px]">
                            <div className="flex items-center justify-center space-x-2">
                              {(user.role === 'vendor' || user.role === 'admin') && (process.env.REACT_APP_ENABLE_VENDOR_OCR_UPLOAD === 'true') && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOcrOrder(order);
                                    setShowOCRModal(true);
                                  }}
                                  className="btn-secondary text-xs px-2 py-1 hover-lift"
                                  title="Upload Documents (OCR)"
                                >
                                  Upload
                                </button>
                              )}

                              {/* Tailless Right Arrow Icon */}
                              <div className="p-0 text-gray-800 group-hover:text-indigo-600 transition-colors flex justify-center">
                                {loadingOrderId === order.id ? (
                                  <svg className="animate-spin h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                ) : (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Quick Actions for Ticket Generation (always visible for admin/accountant) */}
              {(user.role === 'admin' || user.role === 'accountant') && (
                <div ref={quickActionsRef} className="border-t border-gray-200 bg-gray-50 px-4 py-2 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      <span>{selectedOrderIds.size} order{selectedOrderIds.size !== 1 ? 's' : ''} selected</span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedOrderIds(new Set())}
                        disabled={selectedOrderIds.size === 0}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Clear Selection
                      </button>
                      {user.role === 'admin' && (
                        <>
                          <button
                            onClick={() => setShowRejectDialog(true)}
                            disabled={selectedOrderIds.size === 0}
                            className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Reject Selected
                          </button>
                          <button
                            onClick={() => setShowAssignDialog(true)}
                            disabled={selectedOrderIds.size === 0}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Assign to Vendor
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {fetchLoading && orders.length > 0 && (
                <TableLoadingOverlay rows={Math.min(currentOrders.length || ordersPerPage, 8)} />
              )}
            </div>

            {selectedOrder && (
              <OrderModal
                order={selectedOrder}
                isOpen={isOrderModalOpen}
                loadingDetails={selectedOrderLoading}
                onClose={() => {
                  setIsOrderModalOpen(false);
                  // Delay clearing selectedOrder to allow close animation
                  setTimeout(() => {
                    setSelectedOrder(null);
                    setSelectedOrderLoading(false);
                    setLoadingOrderId(null);
                  }, 450);
                }}
                vendors={vendors}
                onAssignOrder={assignOrder}
                onApproveRejectSample={approveRejectSample}
                onFileUpload={handleFileUpload}
                onOrderUpdate={fetchOrders}
                onEditOrder={editOrder}
                onRejectOrder={rejectOrder}
                onCompleteOrder={completeOrder}
                onUploadFullOrderDocument={uploadFullOrderDocument}
              />
            )}

            <OCRUploadModal
              isOpen={showOCRModal}
              onClose={() => {
                setShowOCRModal(false);
                setOcrOrder(null);
              }}
              orderId={ocrOrder?.id}
              onSuccess={() => fetchOrders()}
            />

            {/* Ticket Generation Cart */}
            <TicketGenerationCart
              isOpen={showCart}
              onClose={() => setShowCart(false)}
              selectedOrders={availableOrders}
              onInvoiceGenerated={handleInvoiceGenerated}
            />

            {/* Reject dialog */}
            {showRejectDialog && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                  <h3 className="text-lg font-semibold text-on-light mb-3">Reject Selected Orders</h3>
                  <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded mb-3 text-sm">
                    This action cannot be reversed. Are you sure you want to proceed?
                  </div>
                  <label className="block text-sm font-medium text-on-light mb-1">Cancellation Reason <span className="text-red-500">*</span></label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Please provide a reason for rejecting these orders..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    rows="4"
                    required
                  />
                  <div className="mt-4 flex justify-end space-x-2">
                    <button
                      onClick={() => { setShowRejectDialog(false); setRejectReason(''); }}
                      className="px-4 py-2 text-on-light border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      disabled={!rejectReason.trim() || bulkActionLoading}
                      onClick={handleBulkReject}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {bulkActionLoading ? 'Rejecting...' : 'Confirm Reject'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Assign to Vendor dialog */}
            {showAssignDialog && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                  <h3 className="text-lg font-semibold text-on-light mb-3">Assign Selected Orders</h3>
                  <div className="mb-3 text-sm text-on-light">Select a vendor to assign to {selectedOrderIds.size} selected order{selectedOrderIds.size !== 1 ? 's' : ''}.</div>
                  <label className="block text-sm font-medium text-on-light mb-1">Vendor <span className="text-red-500">*</span></label>
                  <select
                    value={selectedVendorId}
                    onChange={(e) => setSelectedVendorId(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select Vendor</option>
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                    ))}
                  </select>
                  <div className="mt-4 flex justify-end space-x-2">
                    <button
                      onClick={() => { setShowAssignDialog(false); setSelectedVendorId(''); }}
                      className="px-4 py-2 text-on-light border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      disabled={!selectedVendorId || bulkActionLoading}
                      onClick={async () => {
                        try {
                          setBulkActionLoading(true);
                          for (const orderId of Array.from(selectedOrderIds)) {
                            await assignOrder(orderId, selectedVendorId);
                          }
                          const selectedVendorObj = vendors.find(v => String(v.id) === String(selectedVendorId));
                          const vendorName = selectedVendorObj?.name || 'Vendor';
                          orderToasts.assigned(vendorName);
                          setSelectedVendorId('');
                          setShowAssignDialog(false);
                          setSelectedOrderIds(new Set());
                          fetchOrders();
                        } catch (e) {
                          showToast.error('Failed to assign one or more orders');
                        } finally {
                          setBulkActionLoading(false);
                        }
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {bulkActionLoading ? 'Assigning...' : 'Confirm Assign'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )
      }
    </div >
  );
};

export default OrderManagement;